import useAppView from 'Core/Hooks/useAppView'
import useHTMLBodyScrollbar from 'Core/Hooks/useHTMLBodyScrollbar'
import useMultiDispatch from 'Core/Hooks/useMultiDispatch'
import { useURLQuery } from 'Core/Hooks/useURLQuery'
import { getCurrentOrg } from 'Utils/Helpers'
import { LoadingFeedback, SearchInput } from 'V2Components'
import { fetchSessionLogs, parseLogSessionData } from 'features/sessions'
import _ from 'lodash'
import moment from 'moment'
import { Button, LoadingModal, Typography } from 'procyon-ui'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { Filters } from './components/Filters'
import { LogInfoModal } from './components/LogInfoModal'
import { SessionLogsTable } from './components/SessionLogsTable'

const SessionLogs = () => {
  const [selectedLog, setSelectedLog] = useState(null)
  const [searchhKey, setSearchhKey] = useState('')
  const [sessionLogs, setSessionLogs] = useState([])
  const [nextPageTokens, setNextPageTokens] = useState({}) // Changed to object to hold tokens for multiple resource types
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const [filters, setFilters] = useState({
    StartTime: moment().add(-24, 'hours').format('YYYY-MM-DD'),
    EndTime: moment().format('YYYY-MM-DD'),
    ResoureceType: 'ALL_RESOURCES',
    QueryType: 'ALL_AUDITED',
    UserFilter: []
  })

  //@ts-ignore
  const search = useURLQuery()
  const history = useHistory()
  const { appView } = useAppView()
  //@ts-ignore
  const { sessionKey: sessionID } = useParams()
  const { selectDispatch } = useMultiDispatch([])

  const sessionTime = search.get('sessionTime')
  const resourceType = search.get('resourceType')
  // bollean value is true if the url params exists to fetch a specific log
  const isSingleLogFetchingMode = !!(sessionID && sessionTime && resourceType)

  //fetching status for url provided session log
  const [fetchingSelectedLog, setFetchingSelectedLog] = useState(isSingleLogFetchingMode)
  // if we need to fetch a single log, we disable fetching all logs by default
  const [isFetching, setIsFetching] = useState(!isSingleLogFetchingMode)

  /**
   *
   * @param {Partial<typeof filters>} v
   */
  const handleFilterChange = (v = {}) => {
    setFilters((s) => ({ ...s, ...v }))
  }
  const initiallyFetched = useRef(false)

  /**
   *
   */
  const getAndPopulateSessionLogs = async (rawFilterData, { loadMore = false } = {}) => {
    debugger
    const filterData = { ...rawFilterData }

    // Normalize date strings to Date objects
    if (typeof filterData.StartTime === 'string' && filterData.StartTime.length === 10) {
      // YYYY-MM-DD check
      filterData.StartTime = new Date(moment(filterData.StartTime).startOf('day').toISOString())
    } else {
      filterData.StartTime = new Date(filterData.StartTime)
    }

    if (typeof filterData.EndTime === 'string' && filterData.EndTime.length === 10) {
      // YYYY-MM-DD check
      filterData.EndTime =
        filterData.EndTime === moment().format('YYYY-MM-DD')
          ? new Date()
          : new Date(moment(filterData.EndTime).endOf('day').toISOString())
    } else {
      filterData.EndTime = new Date(filterData.EndTime)
    }

    if (!loadMore) {
      // Only reset on a fresh search (not load more)
      setSessionLogs([])
      setNextPageTokens({})
      setCurrentPage(1)
      setIsFetching(true)
    } else {
      setIsFetchingNextPage(true)
    }

    try {
      const AUDITS = FilterMaps[filterData.QueryType]
      const RSRCS = FilterMaps[filterData.ResoureceType]
      const PAGE_SIZE = 10
      const API_PAGE_SIZE = 50

      const currentCount = loadMore ? sessionLogs.length : 0
      const targetTotal = loadMore
        ? (Math.ceil(currentCount / PAGE_SIZE) + 1) * PAGE_SIZE
        : PAGE_SIZE
      const minToAdd = targetTotal - currentCount

      const newNextPageTokens = { ...nextPageTokens }
      const allFetchedLogs = []

      let activeResourceTypes = [...RSRCS]
      let iterations = 0
      const MAX_ITERATIONS = 5

      while (
        allFetchedLogs.length < minToAdd &&
        activeResourceTypes.length > 0 &&
        iterations < MAX_ITERATIONS
      ) {
        iterations++
        const fetchPromises = []

        for (const auditType of AUDITS) {
          for (const rsrcType of activeResourceTypes) {
            const payload = {
              ...filterData,
              ResoureceType: rsrcType,
              QueryType: auditType,
              UserFilter: { Elems: filterData.UserFilter || [] },
              PageSize: API_PAGE_SIZE
            }

            const token = newNextPageTokens[rsrcType]
            if (token) {
              payload.StartAfter = token
            }

            fetchPromises.push(
              fetchSessionLogs(payload).then((logs) => ({
                rsrcType,
                logs: logs.SessionResponses || [],
                nextPageToken: logs.NextPageToken || null
              }))
            )
          }
        }

        const results = await Promise.all(fetchPromises)
        let addedInThisIteration = 0

        results.forEach(({ rsrcType, logs, nextPageToken }) => {
          const filtered = logs.filter((log) => {
            const parsed = parseLogSessionData(log)
            return parsed.some((p) => p && p.Org === getCurrentOrg())
          })
          allFetchedLogs.push(...filtered)
          addedInThisIteration += filtered.length
          newNextPageTokens[rsrcType] = nextPageToken
        })

        // Update active resources for next iteration
        activeResourceTypes = activeResourceTypes.filter((r) => newNextPageTokens[r] !== null)

        // Safety break if we are getting no results but tokens still exist
        if (addedInThisIteration === 0) {
          break
        }
      }

      setNextPageTokens(newNextPageTokens)
      setSessionLogs((prevLogs) => (loadMore ? [...prevLogs, ...allFetchedLogs] : allFetchedLogs))
      if (loadMore) {
        setCurrentPage((page) => page + 1)
      }
    } catch (error) {
      console.error('Failed to fetch session logs', error)
    }

    setIsFetching(false)
    setIsFetchingNextPage(false)
  }

  // // Dont fetch all logs if single log needs to be fetched
  if (!initiallyFetched.current && !isSingleLogFetchingMode) {
    //Fetch initial logs
    getAndPopulateSessionLogs({
      StartTime: moment().add(-24, 'hours'),
      EndTime: moment(),
      ResoureceType: 'ALL_RESOURCES',
      QueryType: 'ALL_AUDITED'
    })
    initiallyFetched.current = true
  }

  const refreshLogs = () => {
    getAndPopulateSessionLogs({
      ...filters,
      StartTime: new Date(filters.StartTime),
      EndTime:
        filters.EndTime === moment().format('YYYY-MM-DD') ? new Date() : new Date(filters.EndTime)
    })
  }

  /**
   * Fetch specific log with data from url
   * @returns
   */
  // https://localhost:3001/ui/admin/session-logs/6eecbc39fe1f03cda4ead57534e36a339126ddacdf21dd13ecdaaa6fc4ddcd14?resourceType=SSH&sessionTime=2024-01-19T10:37:01.722553687Z
  const getLogFromRouteParam = async () => {
    if (!sessionTime || !resourceType || !sessionID) return
    setFetchingSelectedLog(true)
    const logs = await fetchSessionLogs({
      StartTime: new Date(sessionTime),
      //@ts-ignore
      ResoureceType: resourceType,
      QueryType: 'All',
      UserFilter: { Elems: [sessionID] }
    })
    const s = logs.SessionResponses[0]
    setFetchingSelectedLog(false)
    return s
  }

  const getSelectedLog = async () => {
    if (!sessionID) return
    const sessionTime = search.get('sessionTime')
    const resourceType = search.get('resourceType')
    if (sessionTime && resourceType) return await getLogFromRouteParam()
    return _.find(sessionLogs, { SessionKey: sessionID })
  }

  const handleAuditSuccess = () => refreshLogs()

  useHTMLBodyScrollbar(selectedLog)

  useEffect(() => {
    const fn = async () => {
      const l = await getSelectedLog()
      if (l) setSelectedLog(l)
    }
    fn()
  }, [sessionLogs, sessionID])

  useEffect(() => {
    selectDispatch(['deviceList', 'serverList', 'databases', 'kubeClusters'])
  }, [])

  return (
    <div>
      <div className='flex justify-between items-center'>
        <Typography variant='h3'>Session Logs</Typography>
        <div>
          <SearchInput sx={{ width: '280px' }} searchKey={searchhKey} onChange={setSearchhKey} />
        </div>
      </div>
      <div className='bg-[var(--color-common-bg-base)] p-4 mt-8'>
        <Filters
          onApplyClick={getAndPopulateSessionLogs}
          filters={filters}
          onFilterChange={handleFilterChange}
        />
        {!isFetching && (
          <div className='mt-6'>
            <SessionLogsTable
              isFetching={isFetching || isFetchingNextPage}
              searchKey={searchhKey}
              onLogClick={(log) => {
                setSelectedLog(log)
              }}
              sessions={sessionLogs}
              nextPageTokens={nextPageTokens}
              isFetchingNextPage={isFetchingNextPage}
              onNextPage={() => getAndPopulateSessionLogs(filters, { loadMore: true })}
            />
          </div>
        )}
        {isFetching && (
          <div className='h-[400px] flex justify-center items-center'>
            <LoadingModal message='Fetching Logs' shadow={false} />
          </div>
        )}
        {fetchingSelectedLog && (
          <LoadingFeedback
            shrinked
            loading
            message={` Fetching Log of ${moment(sessionTime).format('MMMM Do YYYY, h:mm A')} `}
            caption='Please wait'
            shadow={false}
          />
        )}
        {selectedLog && (
          <LogInfoModal
            onAuditSuccess={() => {
              handleAuditSuccess()
              history.push(`/${appView}/session-logs`)
            }}
            onCancel={() => {
              setSelectedLog(null)
              history.push(`/${appView}/session-logs`)
            }}
            log={selectedLog}
          />
        )}
      </div>
    </div>
  )
}

const FilterMaps = {
  ALL_AUDITED: ['All'],
  Audited: ['Audited'],
  NonAudited: ['NonAudited'],
  ALL_RESOURCES: ['SSH', 'DB', 'K8', 'ECS', 'RDP'],
  SSH: ['SSH'],
  DB: ['DB'],
  K8: ['K8'],
  ECS: ['ECS'],
  RDP: ['RDP']
}

export { SessionLogs }
